import { useContext, useEffect, useState } from "react";

import { Container, Grid, Grid2Props as GridProps } from "@mui/material";

import {
  ActionIcon,
  Badge,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Modal,
  SelectProps,
  Stack,
  Table,
  Text,
  TextProps,
  Tooltip,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconReload, IconX } from "@tabler/icons-react";

import dayjs from "dayjs";

import { Colors } from "../../Lib/data";
import { CourseProps, PaginationProps, PlanProps } from "../../Lib/types";

import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/endpoints";
import { generateRandomString, getEllipsisFromString } from "../../Lib/methods";
import { PaginatedResponse } from "../../Lib/responses";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./styles.scss";

const selectProps: SelectProps = {
  allowDeselect: false,
};
const gridItemProps: GridProps = {
  size: {
    xl: 3,
    lg: 4,
    md: 4,
    sm: 6,
    xs: 12,
  },
};
const Courses: React.FC = () => {
  const context = useContext(AppContext);
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });

  const filterForm = useForm({
    initialValues: {
      isActive: "unspecified",
    },
    validate: {
      isActive: (value) =>
        value && value.length > 0 ? null : "Please select a role",
    },
  });
  const {
    data: plans,
    reloadData: reloadPlans,
    isLoading: isLoadingPlans,
  } = usePerformRequest<PaginatedResponse<PlanProps[]>>({
    route: Endpoints.GetPlans(1, 2000).route,
    method: Endpoints.GetPlans(1, 2000).method,
  });
  const {
    data: courses,
    reloadData: reloadCourses,
    isLoading: isLoadingCourses,
  } = usePerformRequest<PaginatedResponse<PlanProps[]>>({
    route: Endpoints.GetCourses(pagination.page, pagination.limit).route,
    method: Endpoints.GetCourses(pagination.page, pagination.limit).method,
    body:
      filterForm.values.isActive !== "unspecified"
        ? {
            isActive: filterForm.values.isActive,
          }
        : {},
  });
  useEffect(() => {
    reloadCourses();
  }, [filterForm.values.isActive]);

  const [courseImageFiles, setCourseImageFiles] = useState<File[]>([]);
  const courseForm = useForm<CourseProps>({
    initialValues: {
      id: "",
      name: "",
      description: "",
      thumbnail: "",
      images: [],
      planID: "",
      teacherHistory: [],
      isActive: false,
      createdAt: 0,
      createdBy: "",
      teacherID: "",
    },
  });

  const [viewingCourse, setViewingCourse] = useState<CourseProps>();

  const [
    isCourseModalOpened,
    { open: OpenCourseModal, close: CloseCourseModal },
  ] = useDisclosure(false);

  const [isCreatingOrUpdatingCourse, setCreatingOrUpdatingCourse] =
    useState<boolean>(false);

  const textProps: TextProps = {
    fz: 13,
    className: "table-value",
  };
  const showImageCarousel =
    courseImageFiles.length > 0 || courseForm.values.images.length > 0;

  // Type guard to check if an object is CourseProps
  function isCourseProps(obj: any): obj is CourseProps {
    return (
      obj &&
      typeof obj === "object" &&
      "id" in obj &&
      "name" in obj &&
      "description" in obj &&
      "createdAt" in obj &&
      "isActive" in obj
    );
  }
  return (
    <Container maxWidth="lg">
      <div className="courses-container">
        <div className="component-header flex-row align-end justify-between">
          <Text className="label">Courses</Text>
          <div className="flex-row align-center">
            <Button
              color={Colors.PurplePrimary}
              variant="outline"
              onClick={reloadCourses}
              loading={isLoadingCourses}
              rightSection={<IconReload size={16} />}
            >
              Reload
            </Button>
          </div>
        </div>
        <Divider w="100%" my={10} />

        <div className="courses">
          {isLoadingCourses ? (
            <MegaLoader
              size={50}
              containerStyle={{
                marginTop: 45,
                marginBottom: 45,
              }}
            />
          ) : (
            <>
              {!courses?.data || courses?.data.length === 0 ? (
                <DisplayAlert
                  title="No Courses Found"
                  children={<Text>No courses found!</Text>}
                />
              ) : (
                <Grid container spacing={3}>
                  {Array.isArray(courses?.data) &&
                    courses.data
                      .filter(isCourseProps)
                      .filter((t) => t.id !== context?.profile?.id)
                      .map((course, idx) => (
                        <Grid item key={course.id} {...gridItemProps}>
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: idx * 0.07 }}
                          >
                            <Card
                              shadow="xl"
                              padding="lg"
                              radius={12}
                              style={{
                                background:
                                  "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(245,245,255,0.85) 100%)",
                                backdropFilter: "blur(12px)",
                                border: "1.5px solid rgba(102,126,234,0.10)",
                                boxShadow:
                                  "0 8px 32px 0 rgba(102,126,234,0.08), 0 1.5px 4px 0 rgba(118,75,162,0.04)",
                                minHeight: 210,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                              }}
                            >
                              <Stack gap="xs">
                                <Group justify="space-between">
                                  <Text
                                    fw={700}
                                    fz={20}
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent",
                                      backgroundClip: "text",
                                    }}
                                  >
                                    {course.name}
                                  </Text>
                                  <Badge
                                    style={{
                                      background: course.isActive
                                        ? "linear-gradient(135deg, #0b9851 0%, #49ffa3 100%)"
                                        : `linear-gradient(135deg, ${Colors.PurplePrimary} 0%, #a084e8 100%)`,
                                      color: "white",
                                      fontWeight: 600,
                                      borderRadius: 8,
                                    }}
                                  >
                                    {course.isActive ? "Active" : "Inactive"}
                                  </Badge>
                                </Group>
                                <Text c="dimmed" fz={15} lineClamp={2}>
                                  {course.description}
                                </Text>
                                <Text c="dimmed" fz={13}>
                                  Created:{" "}
                                  {dayjs(course.createdAt).format(
                                    "ddd, MMMM DD YYYY"
                                  )}
                                </Text>
                              </Stack>
                              <Group justify="flex-end" mt="md">
                                <Button
                                  size="sm"
                                  radius={8}
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    color: "white",
                                    fontWeight: 600,
                                    boxShadow:
                                      "0 4px 16px rgba(102,126,234,0.10)",
                                  }}
                                  onClick={() => {
                                    OpenCourseModal();
                                    setViewingCourse(
                                      course as unknown as CourseProps
                                    );
                                  }}
                                  rightSection={<IconEye size={16} />}
                                >
                                  View
                                </Button>
                              </Group>
                            </Card>
                          </motion.div>
                        </Grid>
                      ))}
                </Grid>
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        opened={isCourseModalOpened}
        onClose={CloseCourseModal}
        centered
        size="lg"
        withCloseButton={false}
      >
        <div className="course-modal flex-col">
          <div className="flex-row align-center width-100 justify-between">
            <div className="flex-row align-center">
              <Text tt="capitalize" c={Colors.BluePrimary} fw={500} fz={17}>
                View Course
              </Text>
            </div>
            <ActionIcon
              variant="outline"
              color={Colors.BluePrimary}
              onClick={CloseCourseModal}
            >
              <IconX color={Colors.BluePrimary} size={18} />
            </ActionIcon>
          </div>

          {viewingCourse && (
            <>
              <Table mt={20} variant="vertical" layout="fixed" withTableBorder>
                <Table.Thead>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th w={200}>Description</Table.Th>
                    <Table.Td>
                      <CopyButton value={viewingCourse.description}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied!" : "Copy to clipboard"}
                          >
                            <div
                              className="pointer flex-row align-center"
                              onClick={copy}
                              style={{
                                color: copied
                                  ? Colors.PurplePrimary
                                  : Colors.BluePrimary,
                                height: "100%",
                              }}
                            >
                              {getEllipsisFromString(
                                viewingCourse.description,
                                20
                              )}
                            </div>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Table.Td>
                  </Table.Tr>

                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Name</Table.Th>
                    <Table.Td>{viewingCourse.name}</Table.Td>
                  </Table.Tr>

                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Creation Date</Table.Th>
                    <Table.Td>
                      {dayjs(viewingCourse.createdAt).format(
                        "ddd, MMMM DD YYYY"
                      )}
                    </Table.Td>
                  </Table.Tr>
                </Table.Thead>
              </Table>
              <div className="flex-row width-100 align-center">
                <Link to={`/d/course/${viewingCourse.id}`}>
                  <Button mt={20} w={160} color={Colors.PurplePrimary}>
                    Course Details
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Container>
  );
};

export default Courses;
